import { Head } from "@inertiajs/react"
import { Coffee, Users, Beaker, Star, TrendingUp, Calendar } from "lucide-react"
import { useTranslation } from "react-i18next"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"

interface Stats {
  total_tastings: number
  total_samples: number
  total_users: number
  total_cuppings: number
  tastings_this_month: number
  samples_this_month: number
  cuppings_this_month: number
  avg_overall_rating: number
}

interface RecentTasting {
  id: number
  title: string
  user_name: string
  samples_count: number
  created_at: string
}

interface Props {
  stats: Stats
  recent_cuppings: RecentTasting[]
}

export default function MetricsIndex({ stats, recent_cuppings }: Props) {
  const { t } = useTranslation()

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: t("metrics.breadcrumb"),
      href: "/metrics",
    },
  ]
  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={t("metrics.title")} />

      <div className="flex h-full flex-1 flex-col gap-6 p-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold">{t("metrics.title")}</h1>
          <p className="text-muted-foreground">{t("metrics.subtitle")}</p>
        </div>

        {/* Main Stats */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardContent className="flex items-center gap-4 p-4">
              <div className="bg-primary/10 flex h-12 w-12 items-center justify-center rounded-full">
                <Calendar className="text-primary h-6 w-6" />
              </div>
              <div>
                <p className="text-primary text-2xl font-bold">
                  {stats.total_tastings}
                </p>
                <p className="text-muted-foreground text-sm">
                  {t("metrics.stats.total_sessions")}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-4 p-4">
              <div className="bg-primary/10 flex h-12 w-12 items-center justify-center rounded-full">
                <Beaker className="text-primary h-6 w-6" />
              </div>
              <div>
                <p className="text-primary text-2xl font-bold">
                  {stats.total_samples}
                </p>
                <p className="text-muted-foreground text-sm">
                  {t("metrics.stats.total_samples")}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-4 p-4">
              <div className="bg-primary/10 flex h-12 w-12 items-center justify-center rounded-full">
                <Users className="text-primary h-6 w-6" />
              </div>
              <div>
                <p className="text-primary text-2xl font-bold">
                  {stats.total_users}
                </p>
                <p className="text-muted-foreground text-sm">
                  {t("metrics.stats.total_users")}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-4 p-4">
              <div className="bg-primary/10 flex h-12 w-12 items-center justify-center rounded-full">
                <Coffee className="text-primary h-6 w-6" />
              </div>
              <div>
                <p className="text-primary text-2xl font-bold">
                  {stats.total_cuppings}
                </p>
                <p className="text-muted-foreground text-sm">
                  {t("metrics.stats.total_cuppings")}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Monthly Stats */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card className="bg-primary/5">
            <CardContent className="flex items-center gap-4 p-4">
              <TrendingUp className="text-primary h-5 w-5" />
              <div>
                <p className="text-lg font-semibold">
                  {stats.tastings_this_month}
                </p>
                <p className="text-muted-foreground text-xs">
                  {t("metrics.stats.sessions_this_month")}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-primary/5">
            <CardContent className="flex items-center gap-4 p-4">
              <TrendingUp className="text-primary h-5 w-5" />
              <div>
                <p className="text-lg font-semibold">
                  {stats.samples_this_month}
                </p>
                <p className="text-muted-foreground text-xs">
                  {t("metrics.stats.samples_this_month")}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-primary/5">
            <CardContent className="flex items-center gap-4 p-4">
              <TrendingUp className="text-primary h-5 w-5" />
              <div>
                <p className="text-lg font-semibold">
                  {stats.cuppings_this_month}
                </p>
                <p className="text-muted-foreground text-xs">
                  {t("metrics.stats.cuppings_this_month")}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-primary/5">
            <CardContent className="flex items-center gap-4 p-4">
              <Star className="text-primary h-5 w-5" />
              <div>
                <p className="text-lg font-semibold">
                  {stats.avg_overall_rating || "N/A"}
                </p>
                <p className="text-muted-foreground text-xs">
                  {t("metrics.stats.avg_rating")}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Tastings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              {t("metrics.recent_cuppings.title")}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("cuppings.fields.title")}</TableHead>
                  <TableHead>
                    {t("metrics.recent_cuppings.organizer")}
                  </TableHead>
                  <TableHead className="text-center">
                    {t("metrics.recent_cuppings.samples")}
                  </TableHead>
                  <TableHead>{t("metrics.recent_cuppings.date")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recent_cuppings.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      className="text-muted-foreground py-8 text-center"
                    >
                      {t("common.no_results")}
                    </TableCell>
                  </TableRow>
                ) : (
                  recent_cuppings.map((tasting) => (
                    <TableRow key={tasting.id}>
                      <TableCell className="font-medium">
                        {tasting.title}
                      </TableCell>
                      <TableCell>
                        {tasting.user_name || "Desconocido"}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge
                          variant="secondary"
                          className="bg-primary/10 text-primary"
                        >
                          {tasting.samples_count}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {tasting.created_at}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
